import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { toast } from "sonner";
import { pushEvent } from "@/modules/shared/lib/observability";
import { useOrganization } from "@/modules/shared/hooks/useOrganization";

export const DEAL_STAGES = [
  { id: "prospeccao", label: "Prospecção", probability: 10 },
  { id: "qualificacao", label: "Qualificação", probability: 25 },
  { id: "proposta_enviada", label: "Proposta Enviada", probability: 50 },
  { id: "negociacao", label: "Negociação", probability: 70 },
  { id: "contrato_enviado", label: "Contrato Enviado", probability: 90 },
  { id: "ganho", label: "Ganho", probability: 100 },
  { id: "perdido", label: "Perdido", probability: 0 },
] as const;

export type DealStage = (typeof DEAL_STAGES)[number]["id"];

export interface RequestedProfile {
  role: string;
  seniority: string;
  technology: string[];
  quantity: number;
}

export interface CommercialDeal {
  id: string;
  organization_id: string;
  title: string;
  client_name: string | null;
  client_id: string | null;
  stage: DealStage;
  mrr_value: number;
  owner_id: string | null;
  requested_profiles: RequestedProfile[];
  temperature: "alta" | "media" | "baixa";
  next_action: string | null;
  next_action_date: string | null;
  stage_entered_at: string;
  probability_percent: number;
  loss_reason: string | null;
  competitor: string | null;
  client_feedback: string | null;
  crm_deal_id: string | null;
  crm_stage_id: string | null;
  crm_stage_label: string | null;
  crm_pipeline_id: string | null;
  crm_provider: string | null;
  pipeline_id: string | null;
  stage_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  required_skills: string[] | null;
  required_seniority: string | null;
  headcount: number | null;
  last_activity_at: string | null;
  last_activity_label: string | null;
}

export function useCommercialDeals() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["commercial-deals", organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data, error } = await db
        .from("commercial_deals")
        .select("*")
        .eq("organization_id", organizationId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as CommercialDeal[];
    },
    staleTime: 30_000,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deal: Partial<CommercialDeal>) => {
      const stage = deal.stage || "prospeccao";
      const stageInfo = DEAL_STAGES.find((s) => s.id === stage);

      const { data, error } = await db
        .from("commercial_deals")
        .insert({
          title: deal.title!,
          client_name: deal.client_name || null,
          client_id: deal.client_id || null,
          stage,
          mrr_value: deal.mrr_value || 0,
          owner_id: deal.owner_id || null,
          requested_profiles: (deal.requested_profiles as any) || [],
          temperature: deal.temperature || "media",
          next_action: deal.next_action || null,
          next_action_date: deal.next_action_date || null,
          probability_percent: deal.probability_percent ?? stageInfo?.probability ?? 0,
          notes: deal.notes || null,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commercial-deals"] });
      pushEvent("flow.deal.created");
      toast.success("Deal criado com sucesso");
    },
    onError: (err: Error) => {
      toast.error("Erro ao criar deal: " + err.message);
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CommercialDeal> & { id: string }) => {
      // If stage changed, update stage_entered_at
      const updateData: any = { ...updates };
      if (updates.stage) {
        updateData.stage_entered_at = new Date().toISOString();
      }

      const { data, error } = await db
        .from("commercial_deals")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commercial-deals"] });
      pushEvent("flow.deal.updated");
    },
    onError: (err: Error) => {
      toast.error("Erro ao atualizar deal: " + err.message);
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("commercial_deals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commercial-deals"] });
      toast.success("Deal removido");
    },
    onError: (err: Error) => {
      toast.error("Erro ao remover deal: " + err.message);
    },
  });
}
